// Page route — money page transactionnelle (GVA → Verbier, etc.)
import { Suspense, lazy, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeftRight, MapPin } from "lucide-react";
import {
  FaqBlock, JsonLd, RouteBookingCta, RouteHero, RouteLinks, SectionTitle, SeoPage, TrustChips,
  VehicleCards, useHreflang, useSeo, GuideLinks,
} from "@/components/seo/Seo";
import {
  AIRPORTS, BASE_URL, CITIES, LANGS, PLACE_COORDS, S, T, destNote, p, relatedRoutes,
  returnRoute, routeBySlug, routeCopy, routeFaq, routeSlug, routeUrl, routesFrom,
} from "@/lib/seoContent";

const LazyMap = lazy(() => import("@/components/RoutePreviewMap"));
const API = process.env.REACT_APP_BACKEND_URL;

export default function RoutePage({ lang, slug }) {
  const route = routeBySlug(lang, slug);
  const [geometry, setGeometry] = useState(null);
  const t = T[lang];

  const copy = route ? routeCopy(route, lang) : null;
  const path = route ? routeUrl(route, lang) : `/${lang}`;
  const alternates = route ? Object.fromEntries(LANGS.map((l) => [l, routeUrl(route, l)])) : null;

  useSeo({ lang, title: copy ? copy.title : "MILES", description: copy ? copy.meta : "", path, type: "route", id: route ? route.id : "" });
  useHreflang(alternates);

  useEffect(() => {
    setGeometry(null);
    if (!route) return;
    const o = PLACE_COORDS[route.origin];
    const d = PLACE_COORDS[route.destination];
    axios.post(`${API}/api/estimate`, {
      pickup: route.origin, dropoff: route.destination,
      pickup_place: { lat: o[0], lon: o[1] }, dropoff_place: { lat: d[0], lon: d[1] },
    }).then((r) => setGeometry(r.data.route || null)).catch(() => {});
  }, [route]);

  if (!route) return <Navigate to={p(lang, S.routes[lang])} replace />;
  const faq = routeFaq(route, lang);
  const airport = route.type === "airport" ? AIRPORTS[route.origin] : null;
  const back = returnRoute(route);
  const related = relatedRoutes(route);
  const note = destNote(route, lang);

  const crumbs = [
    { label: t.home, to: p(lang) },
    { label: t.routes_title, to: p(lang, S.routes[lang]) },
    ...(airport ? [{ label: airport.name[lang], to: p(lang, S.airport[lang], airport.slug[lang]) }] : []),
    { label: `${copy.originName} → ${copy.destName}` },
  ];

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.label, ...(c.to ? { item: `${BASE_URL}${c.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) },
    { "@context": "https://schema.org", "@type": "Service", serviceType: "Private chauffeur transfer",
      provider: { "@type": "Organization", name: "MILES", url: BASE_URL },
      areaServed: { "@type": "Country", name: "Switzerland" },
      name: `${copy.originName} → ${copy.destName}`,
      ...(copy.price ? { offers: { "@type": "Offer", price: copy.price, priceCurrency: "CHF" } } : {}) },
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId="seo-route-page">
      {jsonLd.map((j, i) => <JsonLd key={i} id={`route-${i}`} data={j} />)}

      <h1 className="mb-6 max-w-3xl text-3xl font-bold leading-tight text-ink sm:text-4xl" data-testid="route-h1">{copy.h1}</h1>
      <RouteHero lang={lang} route={route} copy={copy} />

      <div className="mt-8"><TrustChips lang={lang} ski={route.ski} /></div>

      {geometry && (
        <section className="mt-12" data-testid="route-map">
          <Suspense fallback={<div className="h-72 animate-pulse rounded-3xl bg-cloud" />}>
            <LazyMap geometry={geometry} />
          </Suspense>
          <p className="mt-3 text-xs text-slate">{t.winter_note}</p>
        </section>
      )}

      <div className="mt-12 grid gap-6 md:grid-cols-2">
        <section className="rounded-2xl border border-ink/10 bg-white p-6" data-testid="route-pickup">
          <h2 className="flex items-center gap-2 text-lg font-bold text-ink"><MapPin className="h-5 w-5 text-miles" strokeWidth={1.5} /> {t.pickup_title} — {copy.originName}</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate">
            {airport ? airport.pickup[lang] : t.book_for_other + ". " + copy.intro}
          </p>
        </section>
        <section className="rounded-2xl border border-ink/10 bg-white p-6" data-testid="route-arrival">
          <h2 className="flex items-center gap-2 text-lg font-bold text-ink"><MapPin className="h-5 w-5 text-miles" strokeWidth={1.5} /> {t.dest_title} — {copy.destName}</h2>
          <p className="mt-3 text-sm leading-relaxed text-slate">{note}</p>
        </section>
      </div>

      <section className="mt-12">
        <SectionTitle title={t.vehicles_title} />
        <div className="mt-4"><VehicleCards lang={lang} route={route} /></div>
      </section>

      {back && (
        <section className="mt-12" data-testid="return-trip">
          <Link to={routeUrl(back, lang)}
            className="flex items-center justify-between gap-3 rounded-2xl border border-miles/20 bg-miles-ice/50 px-5 py-4 transition-colors hover:border-miles/50">
            <span className="flex items-center gap-3 text-sm font-semibold text-ink">
              <ArrowLeftRight className="h-4 w-4 text-miles" strokeWidth={1.5} />
              {t.return_trip} : {routeCopy(back, lang).originName} → {routeCopy(back, lang).destName}
            </span>
            <span className="font-mono text-xs text-miles">{routeCopy(back, lang).price ? `${t.price_from} CHF ${routeCopy(back, lang).price}.–` : ""}</span>
          </Link>
        </section>
      )}

      <div className="mt-12 space-y-12">
        {airport && <RouteLinks title={`${t.popular_from} ${airport.name[lang]}`} routes={routesFrom(route.origin).filter((r) => r.id !== route.id)} lang={lang} testId="links-same-airport" />}
        <RouteLinks title={t.related_routes} routes={related} lang={lang} testId="links-related" />
        <GuideLinks lang={lang} path={path} />
        <FaqBlock lang={lang} faq={faq} />
      </div>

      <section className="mt-14 rounded-3xl bg-miles-deep px-7 py-10 text-center sm:px-10" data-testid="route-final-cta">
        <h2 className="text-2xl font-bold text-white">{copy.originName} → {copy.destName}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-white/70">
          {copy.price ? `${t.price_from} CHF ${copy.price}.– · ` : ""}{t.fixed_price} · {t.instant_confirm}
        </p>
        <div className="mx-auto mt-6 max-w-sm">
          <RouteBookingCta lang={lang} route={route} copy={copy} compact />
        </div>
      </section>
    </SeoPage>
  );
}
